import { createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit";
import type { RootState } from "./rootReducer";
import {
  markDirty,
  markManyDirty,
  markPrefsDirty,
  SYNC_TABLES,
} from "./slices/syncSlice";
import { SYNCED_PREF_KEYS } from "./slices/preferencesSlice";

const PUSH_DEBOUNCE_MS = 3000;

// runSync is *injected* by SyncManager via registerRunSync() rather than imported,
// so this file doesn't depend on SyncManager — which would form an import cycle
// (store → syncMiddleware → SyncManager → store). Do NOT switch to a dynamic
// import(): RN/Metro treats import() as a server bundle fetch and throws
// LoadBundleFromServerRequestError. If runSync isn't registered yet, the debounced
// push simply no-ops and the next interval/foreground sync covers it.
let runSync: (() => Promise<void>) | undefined;
export function registerRunSync(fn: () => Promise<void>) {
  runSync = fn;
}

export const listenerMiddleware = createListenerMiddleware();

// Dirty-tracking: after any local mutation, diff each syncable slice's entities
// and mark every added/changed row dirty. Diffing (rather than per-action id
// extraction) handles all our awkward cases uniformly: ids generated inside
// reducers (addRecipe), cascade soft-deletes across many tables (deleteRecipe),
// multi-row set/clear actions, and actions that touch two slices at once
// (updateCookingProgress also mutates recipes).
listenerMiddleware.startListening({
  predicate: (action) => {
    const { type } = action;
    // Ignore redux-persist lifecycle (incl. REHYDRATE — otherwise the whole
    // rehydrated store would be marked dirty), the sync slice's own bookkeeping,
    // and anything the SyncManager applies from the server.
    if (type.startsWith("persist/")) return false;
    if (type.startsWith("sync/")) return false;
    if ((action as { meta?: { fromSync?: boolean } }).meta?.fromSync) return false;
    return true;
  },
  effect: (_action, api) => {
    // Read both states synchronously, before any await (getOriginalState rule).
    const prev = api.getOriginalState() as RootState;
    const next = api.getState() as RootState;

    for (const table of SYNC_TABLES) {
      const prevEntities = prev[table].entities as Record<string, unknown>;
      const nextEntities = next[table].entities as Record<string, unknown>;
      if (prevEntities === nextEntities) continue; // slice untouched this action

      // Collect all changed ids, then mark them dirty in ONE dispatch — a bulk
      // change (clear/add many) must not fan out into N dispatch cycles.
      const ids: string[] = [];
      for (const id in nextEntities) {
        // New row, or its reference changed (update / soft-delete / resurrect).
        if (nextEntities[id] !== prevEntities[id]) ids.push(id);
      }
      if (ids.length) api.dispatch(markManyDirty({ table, ids }));
    }

    // Account-synced preferences (a per-user singleton, not an entity table): if
    // any synced key changed, flag the blob for push. Device-local prefs (view
    // mode, cook mode, resolved locale) are absent from SYNCED_PREF_KEYS and so
    // never trigger a push.
    if (prev.preferences !== next.preferences) {
      for (const key of SYNCED_PREF_KEYS) {
        if (prev.preferences[key] !== next.preferences[key]) {
          api.dispatch(markPrefsDirty());
          break;
        }
      }
    }
  },
});

// Debounced push: a short while after the last local change settles, sync.
// cancelActiveListeners() drops the previous pending run so rapid edits coalesce
// into one sync ~PUSH_DEBOUNCE_MS after the user stops.
listenerMiddleware.startListening({
  matcher: isAnyOf(markDirty, markManyDirty, markPrefsDirty),
  effect: async (_action, api) => {
    api.cancelActiveListeners();
    await api.delay(PUSH_DEBOUNCE_MS);
    await runSync?.();
  },
});
